/**
 * Reset Password Page
 * Allows users to set a new password via email link
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader, CheckCircle, AlertCircle } from 'lucide-react';
import { updatePassword } from '../services/authService';
import { supabase } from '../config/supabase';
import './ResetPassword.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validToken, setValidToken] = useState(true);
  const [checkingToken, setCheckingToken] = useState(true);

  // Check if user has valid recovery token
  useEffect(() => {
    const checkRecoveryToken = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          setValidToken(false);
        }
        
        setCheckingToken(false);
      } catch (err) {
        console.error('Error checking token:', err);
        setValidToken(false);
        setCheckingToken(false);
      }
    };

    checkRecoveryToken();
  }, []);

  // Redirect to login after successful password reset
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        navigate('/login');
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  const validatePassword = () => {
    if (!password) {
      setError('Por favor ingrese una contraseña');
      return false;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }

    if (!confirmPassword) {
      setError('Por favor confirme su contraseña');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validatePassword()) {
      return;
    }

    setLoading(true);

    // Update password
    const { error: updateError } = await updatePassword(password);

    if (updateError) {
      if (updateError.message.includes('expired')) {
        setError('El enlace de restablecimiento ha expirado. Por favor solicite uno nuevo');
      } else {
        setError('Error al actualizar la contraseña. Por favor intente nuevamente.');
      }
      setLoading(false);
      return;
    }

    // Show success message
    setSuccess(true);
    setLoading(false);
  };

  // Show loading while checking token
  if (checkingToken) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-background">
          <img src="/images/paisaje-1.webp" alt="Patagonia Vital" />
          <div className="reset-password-background-overlay"></div>
        </div>
        <div className="reset-password-card">
          <div className="reset-password-loading">
            <Loader className="reset-password-loading-spinner" size={48} />
            <p>Verificando enlace...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if token is invalid
  if (!validToken) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-background">
          <img src="/images/paisaje-1.webp" alt="Patagonia Vital" />
          <div className="reset-password-background-overlay"></div>
        </div>
        <div className="reset-password-card">
          <div className="reset-password-logo">
            <img src="/images/Logo.png" alt="Patagonia Vital Logo" />
          </div>
          <div className="reset-password-invalid-icon">
            <AlertCircle size={64} />
          </div>
          <h1 className="reset-password-title">Enlace Inválido o Expirado</h1>
          <p className="reset-password-invalid-text">
            El enlace de restablecimiento ha expirado o no es válido.
          </p>
          <p className="reset-password-invalid-subtext">
            Por favor solicite un nuevo enlace de restablecimiento de contraseña.
          </p>
          <div className="reset-password-actions">
            <Link to="/forgot-password" className="reset-password-button">
              Solicitar Nuevo Enlace
            </Link>
            <Link to="/login" className="reset-password-link">
              Volver al inicio de sesión
            </Link>
          </div>
          <div className="reset-password-footer">
            <p>© 2025 Patagonia Vital. Todos los derechos reservados.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      {/* Background Image */}
      <div className="reset-password-background">
        <img src="/images/paisaje-1.webp" alt="Patagonia Vital" />
        <div className="reset-password-background-overlay"></div>
      </div>

      {/* Reset Password Card */}
      <div className="reset-password-card">
        {/* Logo */}
        <div className="reset-password-logo">
          <img src="/images/Logo.png" alt="Patagonia Vital Logo" />
        </div>

        {!success ? (
          <>
            {/* Title */}
            <h1 className="reset-password-title">Restablecer Contraseña</h1>
            <p className="reset-password-subtitle">
              Ingrese su nueva contraseña
            </p>

            {/* Error Message */}
            {error && (
              <div className="reset-password-error">
                <span>{error}</span>
              </div>
            )}

            {/* Reset Password Form */}
            <form onSubmit={handleSubmit} className="reset-password-form">
              {/* Password Input */}
              <div className="reset-password-input-group">
                <label htmlFor="password" className="reset-password-label">
                  Nueva Contraseña
                </label>
                <div className="reset-password-password-wrapper">
                  <Lock className="reset-password-input-icon" size={20} />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="reset-password-input"
                    disabled={loading}
                    autoComplete="new-password"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="reset-password-password-toggle"
                    disabled={loading}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {password && (
                  <div className="reset-password-strength">
                    <div className={`reset-password-strength-bar ${
                      password.length < 6 ? 'weak' : 
                      password.length < 10 ? 'medium' : 'strong'
                    }`}>
                      <div className="reset-password-strength-fill"></div>
                    </div>
                    <span className="reset-password-strength-text">
                      {password.length < 6 ? 'Débil' : 
                       password.length < 10 ? 'Media' : 'Fuerte'}
                    </span>
                  </div>
                )}
              </div>

              {/* Confirm Password Input */}
              <div className="reset-password-input-group">
                <label htmlFor="confirmPassword" className="reset-password-label">
                  Confirmar Contraseña
                </label>
                <div className="reset-password-password-wrapper">
                  <Lock className="reset-password-input-icon" size={20} />
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repita la contraseña"
                    className="reset-password-input"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="reset-password-password-toggle"
                    disabled={loading}
                    aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="reset-password-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader className="reset-password-button-spinner" size={20} />
                    <span>Actualizando...</span>
                  </>
                ) : (
                  <>
                    <Lock size={20} />
                    <span>Actualizar Contraseña</span>
                  </>
                )}
              </button>
            </form>
          </>
        ) : (
          <>
            {/* Success Message */}
            <div className="reset-password-success-icon">
              <CheckCircle size={64} />
            </div>
            <h1 className="reset-password-title">Contraseña Actualizada</h1>
            <p className="reset-password-success-text">
              Su contraseña ha sido actualizada exitosamente.
            </p>
            <p className="reset-password-success-subtext">
              Será redirigido al inicio de sesión en 3 segundos...
            </p>
            <div className="reset-password-actions">
              <Link to="/login" className="reset-password-button">
                Ir al Inicio de Sesión
              </Link>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="reset-password-footer">
          <p>© 2025 Patagonia Vital. Todos los derechos reservados.</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
